import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { Ref, ref, watch } from 'vue';

import { useUserStore } from '@/stores/useUserStore';
import { fetchProfileUpdate } from '@/helpers/fetchers';

export const useUIVersionAlert = (): {
  isVisible: Ref<boolean>;
  isHiding: Ref<boolean>;
  checkShouldShowAlert: () => void;
  hideAlert: () => void;
  hideAlertPermanently: () => Promise<void>;
} => {
  const isVisible = ref(false);
  const isHiding = ref(false);
  const userStore = useUserStore();

  /**
   * Updates alert visibility based on user preferences.
   * Only updates if user data is loaded to prevent premature hiding.
   */
  const checkShouldShowAlert = (): void => {
    // TODO
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (userStore.user?.show_new_ui_notice != null) {
      isVisible.value = userStore.user.show_new_ui_notice;
    }
  };

  // Watch for changes in user store
  watch(
    () => userStore.user,
    () => {
      checkShouldShowAlert();
    },
    { immediate: true },
  );

  const hideAlert = (): void => {
    isVisible.value = false;
  };

  /**
   * Permanently hides the version alert by updating User Profile.
   * Shows loading state during the request and handles both success and error cases.
   */
  const hideAlertPermanently = async (): Promise<void> => {
    try {
      isHiding.value = true;
      const formData = new FormData();
      const payload: openapi.UserProfileUpdateBodyOpenapi['payload'] = {
        ...userStore.user,
        show_new_ui_notice: false,
      };
      formData.append('payload', JSON.stringify(payload));
      await fetchProfileUpdate(formData);
      userStore.setUser({
        ...userStore.user,
        show_new_ui_notice: false,
      });
      hideAlert();
    } catch (error) {
      isVisible.value = true;
    } finally {
      isHiding.value = false;
    }
  };

  return {
    isVisible,
    isHiding,
    checkShouldShowAlert,
    hideAlert,
    hideAlertPermanently,
  };
};
